import { formatDate } from 'common/services/format'
import { PinKeypadModal } from 'features/Procedure/components/pin-keypad-modal/pin-keypad-modal'
import type { ESignLogEntry } from 'features/Procedure/state/procedure-api.type'

import styles from './e-sign-modal.module.scss'

type Props = {
  isVisible: boolean
  onClose: () => void
  onSubmit: (pin: string) => Promise<boolean> | boolean
  description: string
  pinLength: number
  signHistory?: ESignLogEntry[]
  isLoading?: boolean
}

const getSignerName = (entry: ESignLogEntry): string => {
  return `${entry.fname || ''} ${entry.lname || ''}`.trim() || 'Unknown'
}

const getSignerInitials = (entry: ESignLogEntry): string => {
  const fullName = getSignerName(entry)

  return fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export const ESignModal = ({
  isVisible,
  onClose,
  onSubmit,
  description,
  pinLength,
  signHistory = [],
  isLoading = false,
}: Props) => {
  const hasSignHistory = signHistory.length > 0

  return (
    <PinKeypadModal
      isVisible={isVisible}
      pinLength={pinLength}
      onSubmit={onSubmit}
      onClose={onClose}
      subtitle={description}
      maskClosable
      isSubmitting={isLoading}
      busyText='Signing...'
      width={560}
      modalClassNames={{ subtitle: styles.description }}>
      {hasSignHistory && (
        <section className={styles.historySection} aria-label='Previous e-sign history'>
          <div className={styles.historyHeader}>
            <h3 className={styles.historyTitle}>Previous signatures</h3>
            <span className={styles.historyCount}>{signHistory.length}</span>
          </div>
          <div className={styles.historyList}>
            {signHistory.map((entry, index) => {
              const signerName = getSignerName(entry)
              const formattedDate = formatDate(entry.datetime, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })

              return (
                <article key={`${entry.datetime}-${index}`} className={styles.historyItem}>
                  <div className={styles.historyAvatar}>{getSignerInitials(entry)}</div>
                  <div className={styles.historyBody}>
                    <div className={styles.historyMeta}>
                      <div className={styles.historyNameRow}>
                        <span className={styles.historyName}>{signerName}</span>
                        {index === 0 && <span className={styles.historyBadge}>Latest</span>}
                      </div>
                      <span className={styles.historyDate}>{formattedDate}</span>
                    </div>
                    {entry.amendment && (
                      <p className={styles.historyAmendment}>
                        <span className={styles.historyAmendmentLabel}>Amendment:</span>{' '}
                        {entry.amendment}
                      </p>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      )}
    </PinKeypadModal>
  )
}
